import {
  ActionNodeDefinition,
  ConversationNodeDefinition,
  TriggerNodeDefinition,
  WorkflowDefinitionV1,
  WorkflowNodeDefinition,
  WorkflowTransitionDefinition,
} from '../../types/workflow-spec';
import { NodeType, WorkflowConnection, WorkflowNode } from '../../types/workflow';

type WorkflowAgentNodeDefinition =
  | TriggerNodeDefinition
  | ConversationNodeDefinition
  | ActionNodeDefinition;

const inferNodeType = (node: WorkflowNodeDefinition): NodeType => {
  if (node.canvasType) return node.canvasType as NodeType;
  switch (node.nodeClass) {
    case 'trigger':
      return 'trigger';
    case 'conversation':
      return 'ai-agent';
    case 'router':
      return 'condition';
    case 'human-handoff':
      return 'webhook';
    case 'end':
      return 'delay';
    default:
      return 'api-call';
  }
};

const configRecord = (config: Record<string, unknown> | undefined): Record<string, unknown> =>
  config && typeof config === 'object' ? config : {};

/** Prefer persisted agent columns; config is merged but cannot wipe assigned agent fields. */
const mergeAgentFields = (
  node: WorkflowAgentNodeDefinition,
  defaults: { agentVersion: string; extra?: Record<string, unknown> }
) => {
  const config = configRecord(node.config);
  const agentId = String(node.agentId || config.agentId || '');
  const agentVersion = String(node.agentVersion || config.agentVersion || defaults.agentVersion);
  const { agentId: _dropId, agentVersion: _dropVer, ...configRest } = config;
  return {
    agentId,
    agentVersion,
    ...configRest,
    ...defaults.extra,
  };
};

const buildNodeData = (node: WorkflowNodeDefinition): Record<string, unknown> => {
  const base: Record<string, unknown> = { nodeClass: node.nodeClass };

  switch (node.kind) {
    case 'trigger':
      return {
        ...base,
        triggerType: node.triggerType,
        ...mergeAgentFields(node, { agentVersion: '1' }),
      };
    case 'conversation':
      return {
        ...base,
        nodeKind: 'agent',
        ...mergeAgentFields(node, { agentVersion: '1.0.0' }),
      };
    case 'action':
      return {
        ...base,
        ...mergeAgentFields(node, { agentVersion: '1.0.0' }),
      };
    case 'router':
      return { ...base, expression: node.expression, ...node.config };
    case 'human-handoff':
      return { ...base, queueId: node.queueId, teamId: node.teamId, ...node.config };
    case 'end':
      return { ...base, result: node.result };
    default:
      return base;
  }
};

const toWorkflowNode = (node: WorkflowNodeDefinition): WorkflowNode => {
  const type = inferNodeType(node);
  const data = buildNodeData(node);

  if (node.policy) {
    data.policy = node.policy;
  }

  return {
    id: node.id,
    type,
    name: node.name,
    title: node.title,
    summary: node.summary,
    nodeClass: node.nodeClass,
    position: node.position,
    data,
    inputs: node.inputs,
    outputs: node.outputs,
    status: 'idle',
  };
};

const toWorkflowConnection = (transition: WorkflowTransitionDefinition): WorkflowConnection => ({
  id: transition.id,
  sourceNodeId: transition.sourceNodeId,
  sourceOutputId: transition.sourcePortId,
  targetNodeId: transition.targetNodeId,
  targetInputId: transition.targetPortId,
  label: transition.label,
  conditionType: transition.conditionType,
  priority: transition.priority,
  isLoopback: transition.isLoopback,
  metadata: transition.metadata,
});

export const parseWorkflowDefinition = (definition: WorkflowDefinitionV1) => ({
  id: definition.id,
  name: definition.name,
  description: definition.description,
  status: definition.status ?? 'draft',
  nodes: definition.nodes.map(toWorkflowNode),
  connections: definition.transitions.map(toWorkflowConnection),
});
